import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { buildSpinCaption, resolveSpinEventType } from '../lib/spinCaptions';
import { FeaturedBadge } from '../components/FeaturedBadge';
import { useFeatureStatus } from '../lib/useFeatureStatus';
import BackButton from '../components/BackButton';
import { markCameraActive } from '../hooks/useEventCachePrefetch';

// EventDrop 360 Booth — V2 + Kiosk Mode
// ──────────────────────────────────────────────────────────────────────────
// Default flow (`/event/:slug/360`):
//   start (CTA) → recording → processing → playback
//   Self-serve, guest-paced. UNCHANGED from V2 except for the additive
//   QR-on-playback (kiosk-only).
//
// Kiosk flow (`/event/:slug/360?kiosk=1`):
//   attract → start (3-2-1 countdown) → recording → processing → playback
//   (with QR + auto-reset after inactivity) → attract
//   This is the live-experience loop — no human attendant needed. Tap to
//   start, the booth runs the loop, and resets itself for the next guest.
//
// Per the brief: do NOT change render pipeline / upload endpoints / gallery
// integration. Everything new is UI-layer only.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recording duration. 10s aligns with /spin (SpinPocketPage) — single
// canonical length for both Spin Film 360 entry points.
const MAX_RECORD_SECONDS = 10;
const DEFAULT_RECORD_SECONDS = 10;
const POLL_INTERVAL_MS = 1500;
// Transient 5xx/network errors tolerated before surfacing to the user.
// Production origin can briefly 520 while the worker warms up / restarts.
const POLL_TRANSIENT_RETRY_CAP = 8;
// Total poll budget — production CPU is much slower than preview; 120s
// covers a 30-40s render plus cellular latency.
const POLL_TOTAL_TIMEOUT_MS = 180_000;   // 180s ceiling — production's constrained CPU can take 60-90s for the cinematic 1080×1920 composite on a cold worker; this gives generous headroom while still failing visibly if the worker dies
const KIOSK_RESET_MS = 22_000;     // playback inactivity → reset
const COUNTDOWN_FROM = 3;          // 3, 2, 1 → record

const Booth360Page = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kioskMode = searchParams.get('kiosk') === '1';

  const [event, setEvent] = useState(null);
  const [eventError, setEventError] = useState(null);
  // Default flow uses 'start' as initial; kiosk uses 'attract' so the
  // booth idles into the attract loop without a guest tap.
  const [screen, setScreen] = useState(kioskMode ? 'attract' : 'start');
  const [seconds, setSeconds] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [boothId, setBoothId] = useState(null);
  const [boothStatus, setBoothStatus] = useState(null);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  const videoPreviewRef = useRef(null);
  const playbackRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const stopAtSecRef = useRef(DEFAULT_RECORD_SECONDS);
  const fileInputRef = useRef(null);
  const resetTimerRef = useRef(null);
  // CRITICAL: keep a live ref to `event` so closures captured by
  // `useCallback` (beginRecording → rec.onstop → uploadClip) read the
  // CURRENT event, not the null value from the first render. Without
  // this, after a successful 10-second recording, `uploadClip` would
  // see `event = null` and bail out with "Event not loaded yet." —
  // exactly the regression reported.
  const eventRef = useRef(null);

  // Mark / unmark camera-active so the ambient prefetch layer
  // (useEventCachePrefetch) pauses Tier-2 work while the booth is
  // actively eating GPU/CPU on a MediaRecorder. Cleared on unmount
  // and whenever the screen transitions back to start/share.
  useEffect(() => {
    const live = screen === 'recording' || screen === 'preview' || screen === 'processing';
    markCameraActive(live);
    return () => markCameraActive(false);
  }, [screen]);

  // ── Resolve event by slug ──────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (active) {
          setEvent(r.data);
          eventRef.current = r.data;   // keep ref in lockstep
        }
      } catch (e) {
        if (active) setEventError(e?.response?.status === 404 ? 'not-found' : 'fetch-failed');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  // ── Cleanup helpers ────────────────────────────────────────────────
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCameraReady(false);
  }, []);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopCamera();
    clearResetTimer();
  }, [stopCamera, clearResetTimer]);

  // Re-attach the live stream to the persistent <video> whenever a
  // new stream is acquired (or, defensively, whenever the camera
  // becomes ready). With the single-video architecture this should
  // only run on initial acquire + after a re-acquire on "Spin again",
  // never during a phase change — which is exactly the point.
  useEffect(() => {
    const stream = streamRef.current;
    const el = videoPreviewRef.current;
    if (!stream || !el) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
      el.play().catch(() => {});
      // Safari occasionally drops the first play() — kick it again.
      setTimeout(() => {
        if (videoPreviewRef.current && videoPreviewRef.current.paused) {
          videoPreviewRef.current.play().catch(() => {});
        }
      }, 120);
    }
  }, [cameraReady]);

  // ── Track helper (kiosk events only — fire-and-forget) ────────────
  const track = useCallback((evt, id) => {
    if (!kioskMode) return;
    const target = id || boothId;
    if (!target) return;
    fetch(`${API}/booth/360/${target}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: evt }),
      keepalive: true,
    }).catch(() => {});
  }, [kioskMode, boothId]);

  // ── Start camera (used by countdown OR direct-to-record) ─────────
  // Bulletproof pattern: tear down any previous stream first, then
  // re-request, then re-attach with a small mount-retry loop. Same
  // fix as the Spin Pocket page — guests who do multiple boomerangs
  // in one session need a clean stream per take.
  const acquireCamera = useCallback(async () => {
    // Stop existing stream + clear srcObject so a stale handle can't
    // produce a black <video>.
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
      streamRef.current = null;
    }
    if (videoPreviewRef.current && videoPreviewRef.current.srcObject) {
      try {
        videoPreviewRef.current.srcObject.getTracks().forEach((t) => t.stop());
      } catch {}
      videoPreviewRef.current.srcObject = null;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
    } catch (e) {
      console.error('[booth-360] camera failed', e);
      setError('Camera unavailable. Use the upload option below.');
      setScreen(kioskMode ? 'attract' : 'start');
      return null;
    }
    streamRef.current = stream;
    setCameraReady(true);
    // Attach with a short retry loop — the recording <video> may not
    // be mounted yet at the moment of the call (countdown phase).
    for (let attempt = 0; attempt < 6; attempt += 1) {
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        try { await videoPreviewRef.current.play(); } catch {}
        setTimeout(() => {
          if (videoPreviewRef.current && videoPreviewRef.current.paused) {
            videoPreviewRef.current.play().catch(() => {});
          }
        }, 120);
        return stream;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    return stream;
  }, [kioskMode]);

  // ── Recording ─────────────────────────────────────────────────────
  const beginRecording = useCallback((stream) => {
    if (!stream) return;
    setSeconds(0);
    chunksRef.current = [];
    try {
      const mime = ['video/mp4;codecs=h264', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm']
        .find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'video/mp4' });
        // Transition screen FIRST so even if upload's early-returns or
        // throws, the user never stares at a stale "Asking camera
        // permission…" overlay (stopCamera below nukes cameraReady).
        setScreen('processing');
        stopCamera();
        await uploadClip(blob, rec.mimeType || 'video/mp4');
      };
      rec.start();
      setScreen('recording');

      let s = 0;
      timerRef.current = setInterval(() => {
        s += 1;
        setSeconds(s);
        if (s >= stopAtSecRef.current) {
          if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
          try {
            if (rec.state !== 'inactive') {
              rec.stop();   // fires rec.onstop → screen→processing
            } else {
              // Recorder went inactive without onstop firing (browser
              // quirk). Bail out gracefully so the guest isn't stuck
              // on the Asking-camera overlay forever.
              stopCamera();
              setError('Recording stopped early — please try again.');
              setScreen(kioskMode ? 'attract' : 'start');
            }
          } catch {}
        }
      }, 1000);
    } catch (e) {
      console.error('[booth-360] record start failed', e);
      stopCamera();
      setError('Recording failed.');
      setScreen(kioskMode ? 'attract' : 'start');
    }
  }, [kioskMode, stopCamera]);

  // ── Default-flow start: open camera preview (no auto-record) ─────
  // Guest taps the CTA → camera permission → live preview shows with
  // a "Start recording" button. Recording only begins on a SECOND tap.
  // This gives the guest a chance to frame the shot and avoids the
  // jarring "tap and you're already recording" feedback.
  const openCameraForPreview = useCallback(async () => {
    setError(null);
    setScreen('preview');
    const s = await acquireCamera();
    // If permission is denied, acquireCamera reverts the screen + sets
    // an error already — nothing else to do here.
    if (!s) return;
  }, [acquireCamera]);

  // Once the user taps "Start recording" on the preview screen, kick
  // off the actual MediaRecorder. The stream is already attached.
  const startRecordingFromPreview = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) {
      // Stream lost between preview and tap — re-acquire and retry.
      openCameraForPreview();
      return;
    }
    beginRecording(stream);
  }, [beginRecording, openCameraForPreview]);

  // (Legacy direct-start kept for kiosk + tests that still expect the
  // pre-Feb-2026 behaviour.)
  const startRecordingDirect = useCallback(async () => {
    setError(null);
    setScreen('recording');
    const s = await acquireCamera();
    if (s) beginRecording(s);
  }, [acquireCamera, beginRecording]);

  // ── Kiosk flow: 3-2-1 countdown, then record ─────────────────────
  const startKioskCountdown = useCallback(async () => {
    setError(null);
    setScreen('start');
    setCountdown(COUNTDOWN_FROM);
    const s = await acquireCamera();
    if (!s) return;
    let n = COUNTDOWN_FROM;
    const tick = () => {
      n -= 1;
      if (n > 0) {
        setCountdown(n);
        setTimeout(tick, 900);
      } else {
        setCountdown(null);
        beginRecording(s);
      }
    };
    setTimeout(tick, 900);
    track('countdown_started');
  }, [acquireCamera, beginRecording, track]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();   // fires onstop → screen→processing
        return;
      }
    } catch {}
    // Recorder was never active OR already inactive — onstop won't
    // fire so we have to transition the screen manually, otherwise
    // the user is stuck on the Asking-camera overlay.
    stopCamera();
    setError('Recording didn\'t capture — please try again.');
    setScreen(kioskMode ? 'attract' : 'start');
  }, [kioskMode, stopCamera]);

  // ── File-picker fallback ─────────────────────────────────────────
  const onFilePicked = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) {
      setError('Please pick a video file.');
      return;
    }
    await uploadClip(f, f.type);
    // Reset the input so picking the same file twice still fires onChange
    if (e.target) e.target.value = '';
  }, []);

  // ── Upload + poll ────────────────────────────────────────────────
  // Reads `event` through a ref so the closure captured by
  // `rec.onstop` always sees the live event, not the null we had at
  // mount. Empty dep array → stable identity → no stale-closure bug.
  const uploadClip = useCallback(async (blobOrFile, mime) => {
    setScreen('processing');
    const ev = eventRef.current;
    if (!ev?.event_id) {
      setError('Event not loaded yet.');
      setScreen('failed');
      return;
    }
    setBoothStatus({ status: 'uploading' });
    try {
      const fd = new FormData();
      const filename = mime.includes('webm') ? 'capture.webm' : 'capture.mp4';
      fd.append('file', blobOrFile, filename);
      const r = await axios.post(`${API}/booth/360/${ev.event_id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const id = r.data?.booth_id;
      if (!id) throw new Error('No booth_id in response');
      setBoothId(id);
      setBoothStatus({ status: 'processing' });

      const started = Date.now();
      let transientErrors = 0;   // swallow brief 5xx / network blips
      const tick = async () => {
        try {
          const sr = await axios.get(`${API}/booth/360/${id}`, { timeout: 8000 });
          transientErrors = 0;
          setBoothStatus(sr.data);
          if (sr.data?.status === 'ready') {
            setScreen('playback');
            return;
          }
          if (sr.data?.status === 'failed') {
            setError(sr.data?.error || 'We hit a snag making your boomerang.');
            setScreen(kioskMode ? 'attract' : 'failed');
            return;
          }
          if (Date.now() - started > POLL_TOTAL_TIMEOUT_MS) {
            setError('Still creating your video — try again in a moment.');
            setScreen(kioskMode ? 'attract' : 'failed');
            return;
          }
          setTimeout(tick, POLL_INTERVAL_MS);
        } catch (pollErr) {
          // Transient origin-restart / edge 5xx — keep polling silently.
          transientErrors += 1;
          // eslint-disable-next-line no-console
          console.warn(`[booth-360] poll error ${transientErrors}/${POLL_TRANSIENT_RETRY_CAP}`, pollErr?.response?.status || pollErr?.message);
          if (Date.now() - started > POLL_TOTAL_TIMEOUT_MS) {
            setError('Still creating your video — try again in a moment.');
            setScreen(kioskMode ? 'attract' : 'failed');
            return;
          }
          if (transientErrors >= POLL_TRANSIENT_RETRY_CAP) {
            setError('Still creating your video — try again in a moment.');
            setScreen(kioskMode ? 'attract' : 'failed');
            return;
          }
          setTimeout(tick, POLL_INTERVAL_MS + (transientErrors * 300));
        }
      };
      setTimeout(tick, POLL_INTERVAL_MS);
    } catch (e) {
      console.error('[booth-360] upload failed', e);
      setError(e?.response?.data?.detail || 'Upload failed. Please try again.');
      setScreen(kioskMode ? 'attract' : 'failed');
    }
  }, [kioskMode]);   // intentionally NOT depending on `event` — read via eventRef

  // ── Reset (kiosk) ────────────────────────────────────────────────
  const resetForAnother = useCallback(() => {
    clearResetTimer();
    setSeconds(0);
    setCountdown(null);
    if (kioskMode) {
      track('reset', boothId);
      setBoothId(null);
      setBoothStatus(null);
      setError(null);
      setScreen('attract');
    } else {
      setBoothId(null);
      setBoothStatus(null);
      setError(null);
      setScreen('start');
    }
  }, [kioskMode, track, boothId, clearResetTimer]);

  // ── Kiosk inactivity timer on playback ───────────────────────────
  useEffect(() => {
    if (!kioskMode) return;
    if (screen !== 'playback') {
      clearResetTimer();
      return;
    }
    resetTimerRef.current = setTimeout(() => {
      resetForAnother();
    }, KIOSK_RESET_MS);
    return clearResetTimer;
  }, [screen, kioskMode, resetForAnother, clearResetTimer]);

  // ── Render ───────────────────────────────────────────────────────
  if (eventError === 'not-found') return <NotFoundView />;

  return (
    <div
      data-testid="booth-360-page"
      data-kiosk={kioskMode ? '1' : '0'}
      style={{
        minHeight: '100svh', background: '#000', color: '#FFFFFF',
        fontFamily: 'Helvetica, Arial, sans-serif',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Universal back-nav — surfaces on every screen state EXCEPT
          recording (no mid-take exits) and EXCEPT kiosk mode (the booth
          is unattended; a back tap would strand the operator). Stops
          propagation in case the underlying layer captures clicks. */}
      {!kioskMode && screen !== 'recording' && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 18, left: 18, zIndex: 50,
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          <BackButton
            fallbackHref={slug ? `/event/${slug}` : '/'}
            label="Back"
            variant="subtle"
            testId="booth360-back-btn"
          />
        </div>
      )}
      {/* ──────────────────────────────────────────────────────────────
          PERSISTENT CAMERA LAYER
          ──────────────────────────────────────────────────────────────
          ONE <video> element, mounted once, never unmounted across
          screen transitions. This is the single most important
          architectural choice on this page — mobile Safari will kill
          a MediaStream the moment its consuming <video> is detached
          from the DOM, so swapping video elements between preview /
          recording / playback was the root cause of the silent black-
          screen bug.

          Visibility is controlled via opacity (not display:none, which
          would also pause the stream on some Android browsers). The
          camera fades to 0 only on the start screen + during playback
          — every other phase (preview / recording / processing) keeps
          it fully lit so the guest sees themselves continuously. */}
      {(() => {
        const cameraVisible =
          screen === 'preview' ||
          screen === 'recording' ||
          screen === 'processing' ||
          (screen === 'start' && kioskMode && countdown != null);
        return (
          <div
            aria-hidden={!cameraVisible}
            style={{
              position: 'absolute', inset: 0, zIndex: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: cameraVisible ? 1 : 0,
              pointerEvents: 'none',
              transition: 'opacity 0.28s ease',
              background: '#000',
            }}
          >
            <video
              ref={videoPreviewRef}
              playsInline
              muted
              autoPlay
              data-testid="booth-360-camera-video"
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 720,
                maxHeight: '100svh',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
                background: '#000',
              }}
            />
          </div>
        );
      })()}

      <AnimatePresence mode="wait">
        {screen === 'attract' && (
          <AttractScreen
            key="attract"
            event={event}
            onTap={startKioskCountdown}
            onMount={() => track('attract_shown')}
          />
        )}

        {screen === 'start' && !kioskMode && (
          <StartScreen
            key="start"
            event={event}
            eventLoading={!event && !eventError}
            error={error}
            onStart={openCameraForPreview}
            onPickFile={() => fileInputRef.current?.click()}
          />
        )}

        {/* PREVIEW — non-kiosk only. Live camera renders behind via
            the persistent layer above. This is just the framing
            chrome + "Start Recording" CTA. */}
        {screen === 'preview' && !kioskMode && (
          <PreviewOverlay
            key="preview"
            cameraReady={cameraReady}
            onStartRecording={startRecordingFromPreview}
            onCancel={() => { stopCamera(); setScreen('start'); }}
          />
        )}

        {/* Recording overlay — REC pip + timer + Stop button. Camera
            keeps showing through from the persistent layer below. */}
        {(screen === 'recording' || (screen === 'start' && kioskMode && countdown != null)) && (
          <RecordingOverlay
            key="recording"
            seconds={seconds}
            cameraReady={cameraReady}
            countdown={countdown}
            onStop={stopRecording}
            kioskMode={kioskMode}
          />
        )}

        {screen === 'processing' && (
          <ProcessingScreen
            key="processing"
            status={boothStatus}
            onSkip={kioskMode ? null : () => {
              // Render keeps cooking server-side; the boomerang will
              // pop into the event's guest gallery via socket push the
              // moment it's ready. Toast confirms what's about to
              // happen so the guest doesn't think they lost it.
              toast.success("Spinning in the background — we'll add it to the gallery in a moment.");
              navigate(`/e/${slug}`);
            }}
          />
        )}

        {/* FAILED — gives the guest a clear "Try again" instead of
            silently bouncing them to the start screen. Per the P0
            brief: never auto-reset to start unless the user explicitly
            cancels. */}
        {screen === 'failed' && (
          <FailedScreen
            key="failed"
            error={error}
            onRetry={() => { setError(null); resetForAnother(); }}
          />
        )}

        {screen === 'playback' && (
          <PlaybackScreen
            key="playback"
            playbackRef={playbackRef}
            videoUrl={boothStatus?.video_url}
            boothId={boothId}
            onAnother={resetForAnother}
            event={event}
            kioskMode={kioskMode}
            onQrRendered={() => track('qr_rendered')}
          />
        )}
      </AnimatePresence>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        capture="user"
        onChange={onFilePicked}
        style={{ display: 'none' }}
        data-testid="booth-360-file-input"
      />
    </div>
  );
};

// ── FAILED — owned-failure screen (vs silent bounce-to-start) ─────────
//
// Surfaces upload / render failures with a clear retry. Mirrors the
// SpinPocketPage `PocketFailed` component so both Spin entry points
// behave identically when the production CPU/edge hits a hiccup.
const FailedScreen = ({ error, onRetry }) => (
  <motion.div
    data-testid="booth-360-failed"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: '#000',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 24, padding: '32px 24px', color: '#FFFFFF',
    }}
  >
    <div style={{
      width: 60, height: 60, borderRadius: '50%',
      border: '1.5px solid rgba(255,138,138,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 26, color: '#FFB4B4',
    }}>!</div>
    <p style={{
      margin: 0, fontFamily: 'Georgia, serif', fontSize: 22,
      letterSpacing: '0.04em', textAlign: 'center', maxWidth: 320,
      color: 'rgba(255,255,255,0.92)',
    }}>
      That didn't quite spin.
    </p>
    <p
      data-testid="booth-360-failed-message"
      style={{
        margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.55)',
        maxWidth: 320, textAlign: 'center', lineHeight: 1.55,
      }}
    >
      {error || 'Something interrupted the render.'}
    </p>
    <button
      data-testid="booth-360-retry-btn"
      onClick={onRetry}
      style={{
        marginTop: 8,
        padding: '14px 32px', borderRadius: 999,
        background: '#FFFFFF', color: '#000000',
        border: 'none', fontSize: 15, fontWeight: 700,
        letterSpacing: '0.05em', cursor: 'pointer',
        boxShadow: '0 12px 28px rgba(255,255,255,0.2)',
      }}
    >
      Try again
    </button>
  </motion.div>
);

// ── ATTRACT (kiosk only) ─────────────────────────────────────────────
const ATTRACT_ROTATING = [
  'Step in. Make your moment.',
  'Grab your friends ✦',
  'Tap to spin.',
];

const AttractScreen = ({ event, onTap, onMount }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    onMount?.();
    const t = setInterval(() => setIdx((i) => (i + 1) % ATTRACT_ROTATING.length), 3600);
    return () => clearInterval(t);
  }, [onMount]);

  return (
    <motion.div
      key="attract-root"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      onClick={onTap}
      data-testid="booth-360-attract"
      style={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', cursor: 'pointer', padding: 22,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Soft radial glow that breathes — sets the "live experience"
          tone before the guest taps. Pure decoration. */}
      <motion.div
        aria-hidden="true"
        animate={{ opacity: [0.45, 0.85, 0.45], scale: [0.94, 1.02, 0.94] }}
        transition={{ duration: 5.6, ease: 'easeInOut', repeat: Infinity }}
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(85vw, 640px)', height: 'min(85vw, 640px)',
          background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(184,151,98,0.08) 40%, transparent 70%)',
          filter: 'blur(28px)', pointerEvents: 'none',
        }}
      />

      <motion.h1
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 'clamp(38px, 8vw, 64px)',
          fontWeight: 400, letterSpacing: '-0.02em',
          margin: 0, lineHeight: 1.1, color: '#FFFFFF',
          position: 'relative', zIndex: 1,
        }}
      >
        🎬 Spin Film 360
      </motion.h1>

      {/* Rotating subtitle */}
      <div style={{ height: 44, marginTop: 18, display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <AnimatePresence mode="wait">
          <motion.p
            key={idx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.6 }}
            style={{
              margin: 0, fontSize: 17, color: 'rgba(255,255,255,0.78)',
              letterSpacing: '0.02em',
            }}
          >
            {ATTRACT_ROTATING[idx]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* CTA pill — gently pulses every 5s */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{
          opacity: 1, scale: 1,
        }}
        transition={{ duration: 0.6, delay: 0.5 }}
        style={{
          marginTop: 38, position: 'relative', zIndex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        }}
      >
        <motion.button
          data-testid="booth-360-attract-cta"
          onClick={(e) => { e.stopPropagation(); onTap(); }}
          animate={{ boxShadow: [
            '0 16px 40px rgba(184,151,98,0.5), 0 0 0 0 rgba(255,255,255,0.0)',
            '0 16px 50px rgba(0,212,255,0.6), 0 0 0 18px rgba(184,151,98,0.0)',
            '0 16px 40px rgba(184,151,98,0.5), 0 0 0 0 rgba(255,255,255,0.0)',
          ] }}
          transition={{ duration: 4.2, ease: 'easeInOut', repeat: Infinity }}
          style={{
            // KIOSK MODE — 78px tall, 22px font, gradient. Reads at
            // 2-3m distance per the Event Visibility brief.
            minHeight: 78,
            padding: '0 48px',
            background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)',
            color: '#FFFFFF',
            border: 'none', borderRadius: 999,
            fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 12,
          }}
        >
          ✦ Tap to start
        </motion.button>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>
          10s · cinematic reel
        </p>
      </motion.div>

      {event?.name && (
        <p style={{
          position: 'absolute', bottom: 26, left: 0, right: 0,
          textAlign: 'center', margin: 0,
          fontSize: 11, color: 'rgba(255,255,255,0.32)',
          letterSpacing: '0.22em', textTransform: 'uppercase',
        }}>
          {event.name}
        </p>
      )}
    </motion.div>
  );
};

// ── START (default-flow only) ────────────────────────────────────────
const StartScreen = ({ event, eventLoading, error, onStart, onPickFile }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    style={{
      minHeight: '100svh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 22px', textAlign: 'center', gap: 24,
    }}
  >
    <motion.p
      initial={{ y: -8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.15, duration: 0.5 }}
      style={{
        fontSize: 11, letterSpacing: '0.28em', color: '#D4AF37',
        textTransform: 'uppercase', margin: 0,
      }}
    >
      ✦ EventDrop 360 Booth
    </motion.p>
    <motion.h1
      initial={{ y: 12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.25, duration: 0.6 }}
      style={{
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: 'clamp(34px, 8vw, 52px)',
        fontWeight: 400, letterSpacing: '-0.02em',
        margin: 0, lineHeight: 1.15, maxWidth: 520,
      }}
    >
      Spin into the moment.
    </motion.h1>
    <motion.p
      initial={{ y: 12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.35, duration: 0.6 }}
      style={{
        fontSize: 15, color: 'rgba(255,255,255,0.65)',
        margin: 0, maxWidth: 360, lineHeight: 1.55,
      }}
    >
      Tap record. Move together slowly. We'll turn it into a beautiful
      boomerang moment {event?.name ? `for ${event.name}` : 'for the event'}.
    </motion.p>
    <motion.button
      data-testid="booth-360-start-btn"
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.5, type: 'spring', stiffness: 260, damping: 20 }}
      onClick={onStart}
      disabled={eventLoading}
      style={{
        // EVENT VISIBILITY — 72px, gradient, glow. Replaces the
        // previous flat white pill that vanished in dark venues.
        minHeight: 72,
        display: 'inline-flex', alignItems: 'center', gap: 12,
        padding: '0 36px',
        background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)',
        color: '#FFFFFF',
        border: 'none', borderRadius: 999,
        fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em',
        cursor: eventLoading ? 'wait' : 'pointer',
        opacity: eventLoading ? 0.5 : 1,
        boxShadow: '0 18px 50px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset',
      }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: 999, background: '#FFFFFF',
        boxShadow: '0 0 12px rgba(255,255,255,0.6)',
        display: 'inline-block',
      }} />
      Start Recording
    </motion.button>
    <motion.button
      data-testid="booth-360-fallback-upload-btn"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.7 }}
      onClick={onPickFile}
      style={{
        background: 'transparent', color: 'rgba(255,255,255,0.55)',
        border: 'none', fontSize: 13, letterSpacing: '0.08em',
        textDecoration: 'underline', textUnderlineOffset: 4,
        cursor: 'pointer', padding: '6px 10px',
      }}
    >
      or upload a clip you already shot
    </motion.button>
    {error && (
      <p data-testid="booth-360-error" style={{
        color: '#FF8A8A', fontSize: 13, marginTop: 8,
        background: 'rgba(255,138,138,0.08)',
        border: '1px solid rgba(255,138,138,0.24)',
        padding: '10px 14px', borderRadius: 12, maxWidth: 360,
      }}>
        {error}
      </p>
    )}
  </motion.div>
);

// ── PREVIEW (Feb 2026) — Live camera with manual "Start recording"
// button. Shown only in non-kiosk mode after the guest taps the start
// CTA. Lets them frame their shot before the 6-second timer kicks in.
// ── PREVIEW OVERLAY (Feb 2026) — pure overlay, no <video> ─────────────
//
// The persistent camera layer at the page root renders the live feed.
// This overlay just adds the framing guide + "Start Recording" CTA on
// top, so React never has to swap video elements between phases.
const PreviewOverlay = ({ cameraReady, onStartRecording, onCancel }) => (
  <motion.div
    data-testid="booth-360-preview-screen"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
    style={{
      position: 'absolute', inset: 0, zIndex: 10,
      minHeight: '100svh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'flex-end',
      gap: 18, padding: '24px 18px 36px',
      background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)',
      pointerEvents: 'none',
    }}
  >
    {/* Asking-permission state — only shown until cameraReady flips */}
    {!cameraReady && (
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.6)', fontSize: 13, pointerEvents: 'none',
      }}>
        Asking camera permission…
      </div>
    )}

    {/* Subtle circular framing guide */}
    {cameraReady && (
      <motion.div
        aria-hidden="true"
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: [0.97, 1.01, 0.97], opacity: [0.18, 0.32, 0.18] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{
          width: 'min(78vw, 520px)', aspectRatio: '1 / 1', borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.55)',
          boxShadow: '0 0 0 1px rgba(212,175,55,0.18) inset',
        }} />
      </motion.div>
    )}

    <p style={{
      margin: 0, marginBottom: 8,
      color: 'rgba(255,255,255,0.92)', fontSize: 16, fontWeight: 600,
      letterSpacing: '-0.01em', textAlign: 'center',
      textShadow: '0 2px 12px rgba(0,0,0,0.7)',
      pointerEvents: 'none',
    }}>
      Frame your shot — tap when you're ready
    </p>

    <motion.button
      data-testid="booth-360-start-recording-btn"
      onClick={onStartRecording}
      disabled={!cameraReady}
      whileTap={{ scale: 0.96 }}
      style={{
        pointerEvents: 'auto',
        // EVENT VISIBILITY — 72px tall, gradient red, full-bleed glow.
        minHeight: 72,
        background: cameraReady
          ? 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)'
          : 'rgba(255,255,255,0.18)',
        color: '#FFFFFF',
        border: 'none', borderRadius: 999,
        padding: '0 36px',
        fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em',
        cursor: cameraReady ? 'pointer' : 'default',
        boxShadow: cameraReady ? '0 16px 40px rgba(255,59,92,0.45), 0 0 0 1px rgba(255,255,255,0.1) inset' : 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 12,
        transition: 'background 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: 999, background: '#FFFFFF',
        boxShadow: '0 0 12px rgba(255,255,255,0.6)',
        display: 'inline-block',
      }} />
      Start Recording
    </motion.button>

    <button
      data-testid="booth-360-preview-cancel"
      onClick={onCancel}
      style={{
        pointerEvents: 'auto',
        minHeight: 44,
        background: 'rgba(0,0,0,0.55)',
        color: 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 999,
        fontSize: 14, fontWeight: 600, letterSpacing: '0.02em',
        cursor: 'pointer', padding: '0 22px',
        backdropFilter: 'blur(10px)',
      }}
    >
      Cancel
    </button>
  </motion.div>
);

// ── RECORDING OVERLAY — pure overlay, no <video> ──────────────────────
const RecordingOverlay = ({ seconds, cameraReady, countdown, onStop }) => {
  const remaining = Math.max(0, DEFAULT_RECORD_SECONDS - seconds);
  const isCountingDown = countdown != null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'absolute', inset: 0, zIndex: 10,
        minHeight: '100svh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-end',
        // EVENT VISIBILITY — bottom safe area lifts the Stop button
        // clear of the Android nav bar so guests don't tap it by
        // mistake mid-recording.
        gap: 14,
        paddingTop: 20,
        paddingLeft: 18,
        paddingRight: 18,
        paddingBottom: 'max(40px, env(safe-area-inset-bottom))',
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%)',
        pointerEvents: 'none',
      }}
    >
      {/* Camera renders BELOW from the persistent layer. We just paint
          the chrome (REC pip + timer + countdown + Stop button) on top. */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
        }}
      >
        {!cameraReady && !isCountingDown && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.85)', fontSize: 16, fontWeight: 600,
          }}>
            Asking camera permission…
          </div>
        )}

        {/* Subtle circular guide — bumped from 1px @ 12% opacity to
            3px @ 22% per the Event Visibility brief. Still decorative
            but actually visible in dark venues. */}
        {cameraReady && !isCountingDown && (
          <motion.div
            aria-hidden="true"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: [0.97, 1.01, 0.97], opacity: [0.18, 0.32, 0.18] }}
            transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{
              width: 'min(78vw, 520px)', aspectRatio: '1 / 1', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.55)',
              boxShadow: '0 0 0 2px rgba(212,175,55,0.28) inset, 0 0 32px rgba(0,0,0,0.4)',
            }} />
          </motion.div>
        )}

        {/* Countdown overlay (kiosk only) */}
        <AnimatePresence>
          {isCountingDown && (
            <motion.div
              key={countdown}
              initial={{ scale: 1.3, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.7, opacity: 0 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              data-testid="booth-360-countdown"
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}
            >
              <span style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 'clamp(120px, 30vw, 240px)',
                fontWeight: 400, color: '#FFFFFF',
                textShadow: '0 6px 40px rgba(0,0,0,0.6)',
                letterSpacing: '-0.04em',
              }}>
                {countdown}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {!isCountingDown && (
          <>
            {/* REC pip — top-left, double-sized, strong red glow.
                Per Event Visibility brief: 16x16, 0 0 18px glow,
                pulse animation. Reads at 2m+ in dark venues. */}
            <div style={{
              position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', left: 16,
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(0,0,0,0.7)', padding: '10px 16px',
              borderRadius: 999, backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span data-testid="booth-360-rec-dot" style={{
                width: 16, height: 16, borderRadius: 999, background: '#FF3B5C',
                boxShadow: '0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset',
                animation: 'boothPulse 1.1s ease-in-out infinite',
              }} />
              <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '0.14em' }}>REC</span>
            </div>

            {/* Timer — TOP-CENTRE per the Event Visibility brief.
                Guests look centre, not top-right. 20px font, 800 weight,
                heavy backdrop blur for legibility against any camera bg. */}
            <div data-testid="booth-360-timer" style={{
              position: 'absolute', top: 'max(16px, env(safe-area-inset-top))',
              left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)', padding: '10px 18px',
              borderRadius: 999, fontVariantNumeric: 'tabular-nums',
              fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              color: '#FFFFFF',
            }}>
              0:{String(seconds).padStart(2, '0')} / 0:{String(DEFAULT_RECORD_SECONDS).padStart(2, '0')}
            </div>
            <AnimatePresence>
              {seconds < 4 && (
                <motion.div
                  key="hint"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.4 }}
                  style={{
                    position: 'absolute', bottom: 130, left: '50%', transform: 'translateX(-50%)',
                    fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.95)',
                    background: 'rgba(0,0,0,0.65)', padding: '10px 18px',
                    borderRadius: 999, backdropFilter: 'blur(12px)',
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  ✦ Move together slowly
                </motion.div>
              )}
              {/* Slow-motion cue — appears at the 7s mark per motion
                  spec v2. Tells guests "this is the hero moment, react
                  now". Gold-bordered + spring scale so it lands like a
                  beat drop, not a tooltip. */}
              {seconds >= 7 && seconds < 10 && (
                <motion.div
                  key="slowmo-cue"
                  data-testid="booth-360-slowmo-cue"
                  initial={{ opacity: 0, scale: 0.85, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.92, y: -6 }}
                  transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                  style={{
                    position: 'absolute', bottom: 130, left: '50%', transform: 'translateX(-50%)',
                    fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em',
                    color: '#FFFFFF',
                    background: 'linear-gradient(135deg, rgba(212,175,94,0.35) 0%, rgba(184,151,98,0.35) 100%)',
                    padding: '14px 24px', borderRadius: 999,
                    backdropFilter: 'blur(18px)',
                    whiteSpace: 'nowrap',
                    border: '1.5px solid rgba(212,175,94,0.8)',
                    boxShadow: '0 0 28px rgba(212,175,94,0.45), 0 6px 24px rgba(0,0,0,0.45)',
                  }}
                >
                  ✨ Slow motion starting…
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      {!isCountingDown && (
        <button
          data-testid="booth-360-stop-btn"
          onClick={onStop}
          style={{
            pointerEvents: 'auto',
            // EVENT VISIBILITY — 72px tall, gradient, glow. No more
            // tiny white pill that disappears on a bright capture.
            minHeight: 72,
            padding: '0 36px',
            background: 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)',
            color: '#FFFFFF',
            border: 'none', borderRadius: 999,
            fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em',
            cursor: 'pointer',
            boxShadow: '0 16px 40px rgba(255,59,92,0.45), 0 0 0 1px rgba(255,255,255,0.1) inset',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 10,
          }}
        >
          <span style={{
            width: 14, height: 14, borderRadius: 3, background: '#FFFFFF',
            display: 'inline-block',
          }} />
          Stop &amp; create
        </button>
      )}
      {!isCountingDown && (
        <p style={{
          fontSize: 13, color: 'rgba(255,255,255,0.78)', margin: 0,
          fontWeight: 600, letterSpacing: '0.04em',
          textShadow: '0 2px 8px rgba(0,0,0,0.7)',
        }}>
          Auto-stops at {DEFAULT_RECORD_SECONDS}s · {remaining}s left
        </p>
      )}
      <style>{`
        @keyframes boothPulse {
          0%, 100% { box-shadow: 0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1); }
          50% { box-shadow: 0 0 28px rgba(255,59,92,1), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1.12); }
        }
      `}</style>
    </motion.div>
  );
};

// ── PROCESSING ────────────────────────────────────────────────────────
//
// Per the async-queue architecture spec ("upload should feel instant ·
// the cinematic render should feel magical AFTER upload"): no spinner,
// no anxiety. The shimmer thumbnail + queued copy reads as
// "intentional, queued, almost ready" rather than "broken, slow,
// what's happening".
const ProcessingScreen = ({ onSkip, skipLabel }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5 }}
    style={{
      position: 'absolute', inset: 0, zIndex: 20,
      minHeight: '100svh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 24, padding: '32px 22px', textAlign: 'center',
      background: '#000',
    }}
  >
    {/* Shimmer thumbnail placeholder — pulses on a 1.8s loop, gradient
        sweeps across to imply something cinematic is being assembled.
        Replaces the previous gold-rim spinner (which read as a
        loading/error state, not a "we're crafting your film" state). */}
    <div
      data-testid="booth-360-processing-shimmer"
      style={{
        position: 'relative',
        width: 'min(70vw, 240px)',
        aspectRatio: '9 / 16',
        borderRadius: 18,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(184,151,98,0.18) 0%, rgba(0,212,255,0.12) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 18px 48px rgba(184,151,98,0.18)',
      }}
    >
      <motion.div
        aria-hidden="true"
        initial={{ x: '-120%' }}
        animate={{ x: '120%' }}
        transition={{ duration: 1.8, ease: 'easeInOut', repeat: Infinity }}
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
      <motion.div
        aria-hidden="true"
        animate={{ opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 2.4, ease: 'easeInOut', repeat: Infinity }}
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: 32, color: 'rgba(255,255,255,0.85)',
          letterSpacing: '0.08em',
        }}
      >
        ✦
      </motion.div>
    </div>
    <motion.h2
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.6 }}
      data-testid="booth-360-processing-title"
      style={{
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: 26, fontWeight: 400, letterSpacing: '-0.01em',
        margin: 0, color: '#FFFFFF',
      }}
    >
      ✨ Upload complete
    </motion.h2>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.45, duration: 0.6 }}
      style={{
        fontSize: 15, color: 'rgba(255,255,255,0.78)',
        maxWidth: 320, margin: 0, lineHeight: 1.55, letterSpacing: '0.01em',
      }}
    >
      Your cinematic spin is processing…
    </motion.p>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.7, duration: 0.6 }}
      style={{
        fontSize: 12, color: 'rgba(255,255,255,0.5)',
        maxWidth: 320, margin: 0, lineHeight: 1.55, letterSpacing: '0.04em',
      }}
    >
      This usually takes 10–20 seconds
    </motion.p>

    {/* SKIP THE WAIT — appears 8s in, gives the guest a graceful exit
        if they don't want to stand around. The render keeps cooking
        on the queue worker and the boomerang appears in the gallery
        via socket push the moment it's ready. */}
    {onSkip && (
      <motion.button
        data-testid="booth-360-skip-wait-btn"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 8, duration: 0.6 }}
        whileTap={{ scale: 0.96 }}
        onClick={onSkip}
        style={{
          marginTop: 16,
          background: 'transparent',
          color: 'rgba(255,255,255,0.65)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 999,
          padding: '11px 24px',
          fontSize: 13, fontWeight: 600, letterSpacing: '0.05em',
          cursor: 'pointer',
        }}
      >
        {skipLabel || 'Skip the wait — back to gallery'}
      </motion.button>
    )}
  </motion.div>
);

// ── PLAYBACK + QR (kiosk) ─────────────────────────────────────────────

// Per-platform share button — Event Visibility tokens (56px, glow,
// bold copy), accent-coloured ring tinted to the brand colour of the
// destination. Click handler fires whichever protocol that platform
// understands (wa.me deep link for WhatsApp, upload pages for
// TikTok/Instagram, native share sheet for "More").
const SpinShareButton = ({ testId, label, emoji, accent, onClick }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    style={{
      minHeight: 56, padding: '0 22px',
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: 'rgba(255,255,255,0.06)',
      color: '#FFFFFF',
      border: `1px solid ${accent === '#FFFFFF' ? 'rgba(255,255,255,0.22)' : accent + '55'}`,
      borderRadius: 999,
      fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em',
      cursor: 'pointer',
      boxShadow: `0 0 18px ${accent === '#FFFFFF' ? 'rgba(255,255,255,0.1)' : accent + '22'}`,
      backdropFilter: 'blur(10px)',
    }}
  >
    <span style={{ fontSize: 16, lineHeight: 1 }}>{emoji}</span>
    {label}
  </button>
);

const PlaybackScreen = ({ playbackRef, videoUrl, boothId, onAnother, event, kioskMode, onQrRendered }) => {
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  // Fire-and-forget share telemetry — every tap on a share button
  // pings /api/platform/share-event so we can build the "% guests
  // who shared" funnel without any analytics SDK. Failures are
  // silent — telemetry must never block the actual share.
  const trackShare = (platform, action, bId, eId) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform/share-event`;
      // Use sendBeacon so the request survives the page navigation
      // when the share button opens a new tab.
      const body = JSON.stringify({ platform, action, booth_id: bId || '', event_id: eId || '' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      }
    } catch { /* ignore */ }
  };
  // 1.6s no-UI hold, then title + buttons fade in (V2 reveal moment).
  // QR fades in 0.8s AFTER the buttons.
  const [revealed, setRevealed] = useState(false);
  const [showQr, setShowQr] = useState(false);

  // ── Playback telemetry — feeds the SpreadScoreCard's
  //    avg_watch_time + replay_rate signals. The video has `loop`
  //    set so `onEnded` never fires — we instead detect each loop
  //    cycle via timeupdate jumping BACKWARDS (currentTime drops
  //    significantly = the video just wrapped). One ping per loop
  //    cycle = one "view" in the aggregator; multiple loop cycles
  //    on the same booth_id = "replayed" in replay_rate. ────────
  useEffect(() => {
    if (!boothId || !event?.event_id || !playbackRef?.current) return undefined;
    const video = playbackRef.current;
    let lastT = 0;
    let firedThisCycle = false;
    const sendBeacon = (durationMs, completed) => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform/playback-event`;
        const body = JSON.stringify({
          booth_id: boothId, event_id: event.event_id,
          duration_watched_ms: Math.max(0, Math.round(durationMs)),
          completed,
        });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } else {
          fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
        }
      } catch { /* ignore */ }
    };
    const onTime = () => {
      const t = video.currentTime;
      const d = video.duration || 0;
      // Loop detected: time jumped backwards by more than 1.5s
      if (lastT > 1 && t < lastT - 1.5) {
        sendBeacon((d || lastT) * 1000, true);
        firedThisCycle = false;
      } else if (!firedThisCycle && d > 0 && t >= d * 0.8) {
        // First full watch — 80% threshold catches users who stop
        // before the literal end (mobile fingers on the timeline).
        sendBeacon(d * 1000, true);
        firedThisCycle = true;
      }
      lastT = t;
    };
    // Send a "page-exit" partial ping so even abandoned views
    // contribute to the watch-time average.
    const onPageHide = () => {
      if (video.currentTime > 0.5 && !firedThisCycle) {
        sendBeacon(video.currentTime * 1000, false);
        firedThisCycle = true;
      }
    };
    video.addEventListener('timeupdate', onTime);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      try { video.removeEventListener('timeupdate', onTime); } catch { /* ignore */ }
      window.removeEventListener('pagehide', onPageHide);
      // Final exit ping while the component still mounted
      onPageHide();
    };
  }, [boothId, event?.event_id, playbackRef, videoUrl]);
  useEffect(() => {
    const t1 = setTimeout(() => setRevealed(true), 1600);
    const t2 = setTimeout(() => setShowQr(true), 1600 + 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Notify the backend when the QR first renders.
  useEffect(() => {
    if (showQr && kioskMode && boothId) onQrRendered?.();
  }, [showQr, kioskMode, boothId, onQrRendered]);

  // ── "Featured on the live wall" — the social feedback loop closer.
  // Polls /api/booth/360/{booth_id}/feature-status and fires a
  // toast the FIRST time the spin wins a category. The badge then
  // stays on the playback player (and in the share row) for the rest
  // of the session. Per user brief: this turns a passive guest into
  // an evangelist — the "WE made it" moment. Skip on kiosk: the
  // kiosk is a shared device, no specific guest to acknowledge.
  const onPromoted = useCallback((cats) => {
    if (!cats?.length) return;
    const headline = cats.length === 1
      ? `${cats[0].category_icon || '🔥'} ${cats[0].category_label}`
      : '🔥 Featured on the live wall';
    toast.custom(
      (t) => (
        <div
          data-testid="featured-promotion-toast"
          style={{
            display: 'flex', flexDirection: 'column', gap: 6,
            padding: '16px 22px', borderRadius: 18,
            background: 'linear-gradient(135deg, rgba(20,18,32,0.96) 0%, rgba(40,24,40,0.96) 100%)',
            border: '1px solid rgba(212,175,94,0.55)',
            boxShadow: '0 0 36px rgba(212,175,94,0.45), 0 12px 32px rgba(0,0,0,0.5)',
            color: '#FFFFFF', minWidth: 280, maxWidth: 360,
            backdropFilter: 'blur(20px)',
          }}
          onClick={() => toast.dismiss(t)}
        >
          <span style={{
            fontSize: 13, fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: '#D4AF5E',
            textShadow: '0 0 14px rgba(212,175,94,0.6)',
          }}>
            ✨ Your spin just appeared on the venue screen
          </span>
          <span style={{
            fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.92)',
            letterSpacing: '-0.005em',
          }}>
            {headline}
          </span>
          <span style={{
            fontSize: 12, fontWeight: 500,
            color: 'rgba(255,255,255,0.62)', letterSpacing: '0.02em',
          }}>
            Everyone can see it live right now
          </span>
        </div>
      ),
      { duration: 7000, position: 'top-center' },
    );
  }, []);
  const { categories: featuredCategories } = useFeatureStatus(
    kioskMode ? null : boothId,
    onPromoted,
  );

  const shareUrl = useMemo(() => {
    if (!boothId) return '';
    // Use the host the guest is actually on (e.g. eventdrop.co) so the
    // share caption shows the brand domain, NOT the internal Emergent
    // host that lives in REACT_APP_BACKEND_URL. The /api/booth/360/share
    // route is served at the same origin (Cloudflare → backend) on
    // both production and preview, so this is safe.
    const base = (typeof window !== 'undefined' ? window.location.origin : (BACKEND_URL || '')).replace(/\/$/, '');
    return `${base}/api/booth/360/share/${boothId}`;
  }, [boothId]);

  // ── Platform-aware viral caption (motion spec v3 brief) ────────────
  // Computed once per render — `event` is stable after slug resolution
  // so this is effectively memo-cheap. Returned object is keyed by
  // platform so each share button can grab its own pre-built string.
  // WhatsApp gets the personal/emotional variant; TikTok / Instagram
  // get the full caption + URL.
  //
  // When the spin is currently featured on the live wall, we inject
  // the SINGLE proudest category (first in the list) between the
  // emotional hook and the brand attribution. Authenticity guard:
  // ONLY pass `featured` when the booth actually wins a category —
  // never fabricate prestige. (`featuredCategories` is empty until
  // /feature-status confirms a real win.)
  const captions = useMemo(() => {
    const eventType = resolveSpinEventType(event);
    const eventName = (event?.event_name || '').trim() || null;
    const featured = featuredCategories[0] || null;
    const mk = (platform) => buildSpinCaption({ platform, eventType, eventName, featured });
    return {
      tiktok: `${mk('tiktok')}\n${shareUrl}`,
      instagram: `${mk('instagram')}\n${shareUrl}`,
      whatsapp: `${mk('whatsapp')} ${shareUrl}`,
      generic: mk('generic'),
    };
  }, [event, shareUrl, featuredCategories]);

  const onShare = useCallback(async () => {
    if (!videoUrl && !shareUrl) return;
    const url = shareUrl || videoUrl;

    // ── Web Share API Level 2 — share the VIDEO FILE itself ──────────
    // This is what unlocks TikTok / Instagram / Reels in the native
    // share sheet on iOS Safari + Android Chrome. Sharing just a URL
    // only offers Messages / Mail / clipboard targets — sharing a
    // File reveals the full app picker because the OS recognises
    // it as a video the receiving app can post directly.
    if (videoUrl && navigator.canShare && typeof navigator.share === 'function') {
      try {
        const resp = await fetch(videoUrl, { mode: 'cors' });
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], 'spin-film-360.mp4', { type: 'video/mp4' });
          const payload = {
            files: [file],
            title: 'Spin Film 360',
            text: '✦ A little moment',
          };
          if (navigator.canShare(payload)) {
            await navigator.share(payload);
            return;
          }
        }
      } catch (err) {
        // File-share unsupported / CORS issue — fall through to URL share
        // eslint-disable-next-line no-console
        console.warn('[booth-360] file share fallback', err);
      }
    }

    // Fallback 1: Web Share with URL only (Messages / Mail / clipboard)
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My 360 moment', text: '✦ A little moment', url });
        return;
      } catch {}
    }
    // Fallback 2: copy share URL to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  }, [videoUrl, shareUrl]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        minHeight: '100svh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 22, padding: '32px 18px',
      }}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 220, damping: 22 }}
        style={{
          width: '100%', maxWidth: 480,
          aspectRatio: '1 / 1',
          borderRadius: 22, overflow: 'hidden',
          background: '#0F0F14',
          boxShadow: '0 40px 90px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,175,55,0.18)',
          position: 'relative',
        }}
      >
        {videoUrl && (
          <video
            ref={playbackRef}
            src={videoUrl}
            autoPlay loop playsInline
            controls={revealed}
            data-testid="booth-360-playback-video"
            style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
          />
        )}
        <AnimatePresence>
          {revealed && (
            <motion.div
              key="top-overlay"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              data-testid="booth-360-playback-overlay"
              style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                padding: '18px 16px 36px',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)',
                pointerEvents: 'none', textAlign: 'center',
              }}
            >
              <p style={{
                margin: 0, color: '#FFFFFF',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 18, fontWeight: 400, letterSpacing: '-0.01em',
              }}>
                ✦ Your 360 moment
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {revealed && (
          <motion.div
            key="reveal-cluster"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: 'easeOut' }}
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 18,
            }}
          >
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.05, duration: 0.6 }}
              data-testid="booth-360-reveal-title"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 24, fontWeight: 400, margin: 0, color: '#FFFFFF',
              }}
            >
              ✦ Your moment
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 1.95 }}
              data-testid="booth-360-format-tag"
              style={{
                margin: '-6px 0 6px', fontSize: 11, fontWeight: 700,
                color: 'rgba(212,175,94,0.92)',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                textAlign: 'center',
              }}
            >
              ✨ Cinematic slow-motion reel
            </motion.p>

            {/* "Featured on the live wall" — the prestige acknowledgement.
                Hidden until the spin actually wins a category; renders
                between the format tag and the share row so the guest
                sees it FIRST when they revisit the playback page after
                getting featured. Triggers a toast on the initial
                promotion (see onPromoted above). */}
            {featuredCategories.length > 0 && (
              <FeaturedBadge categories={featuredCategories} />
            )}

            {/* SHARE ROW — Spin Viral Loop per positioning brief.
                Explicit platform buttons (NOT hidden in native share)
                + "Made with EventDrop" attribution + "Try this at
                your own event →" CTA. This is the platform's growth
                engine — every shared spin becomes a marketing surface
                with a one-click path back to /spin. */}
            <div style={{ textAlign: 'center', marginBottom: 4 }}>
              <h3 style={{
                margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 22, fontWeight: 400, color: '#FFFFFF',
                letterSpacing: '-0.01em',
              }}>Share your spin</h3>
              <p style={{
                margin: '6px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.6)',
                letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
              }}>Optimised for TikTok &amp; Reels</p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <SpinShareButton
                testId="booth-360-share-tiktok"
                label="TikTok"
                emoji="🎵"
                accent="#FF0050"
                onClick={() => {
                  trackShare('tiktok', 'open', boothId, event?.event_id);
                  // Pre-copy the TikTok-flavoured caption — TikTok's
                  // upload page has no URL-parameter caption support,
                  // so clipboard pre-fill is the highest-leverage way
                  // to lift share completion.
                  if (navigator.clipboard) navigator.clipboard.writeText(captions.tiktok).catch(() => {});
                  window.open('https://www.tiktok.com/upload', '_blank', 'noopener');
                }}
              />
              <SpinShareButton
                testId="booth-360-share-instagram"
                label="Instagram"
                emoji="📸"
                accent="#E1306C"
                onClick={() => {
                  trackShare('instagram', 'open', boothId, event?.event_id);
                  if (navigator.clipboard) navigator.clipboard.writeText(captions.instagram).catch(() => {});
                  window.open('https://www.instagram.com/', '_blank', 'noopener');
                }}
              />
              <SpinShareButton
                testId="booth-360-share-whatsapp"
                label="WhatsApp"
                emoji="💬"
                accent="#25D366"
                onClick={() => {
                  trackShare('whatsapp', 'share', boothId, event?.event_id);
                  // wa.me already supports inline text — feed it the
                  // personal/emotional variant directly.
                  window.open(`https://wa.me/?text=${encodeURIComponent(captions.whatsapp)}`, '_blank', 'noopener');
                }}
              />
              <SpinShareButton
                testId="booth-360-share-native"
                label={copied ? 'Link copied' : 'More'}
                emoji={copied ? '✓' : '↗'}
                accent="#FFFFFF"
                onClick={() => { trackShare('native', 'share', boothId, event?.event_id); onShare(); }}
              />
              {!kioskMode && (
                <a
                  data-testid="booth-360-download-btn"
                  href={videoUrl}
                  download={`eventdrop-360-${Date.now()}.mp4`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackShare('download', 'download', boothId, event?.event_id)}
                  style={{
                    minHeight: 56, padding: '0 22px',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'transparent',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255,255,255,0.32)',
                    borderRadius: 999, fontSize: 15, fontWeight: 700,
                    textDecoration: 'none', letterSpacing: '-0.01em',
                  }}
                >
                  <span style={{ fontSize: 16 }}>↓</span>
                  Download
                </a>
              )}
            </div>

            {/* COPY CAPTION — platform-aware. Pre-builds a context-
                appropriate caption (wedding / prom / birthday /
                generic) so guests don't have to think about it. Big
                completion-rate lift on platforms with no programmatic
                share URL (Instagram, TikTok). Shows a live preview of
                the text being copied so the guest knows what's
                landing on their clipboard. */}
            <div style={{
              marginTop: 4, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 8, width: '100%', maxWidth: 360,
            }}>
              <div
                data-testid="booth-360-caption-preview"
                style={{
                  width: '100%', padding: '12px 14px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 14, color: 'rgba(255,255,255,0.86)',
                  fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-line',
                  textAlign: 'left', fontWeight: 500,
                }}
              >
                {captions.tiktok}
              </div>
              <button
                data-testid="booth-360-copy-caption"
                onClick={async () => {
                  try {
                    if (navigator.clipboard) await navigator.clipboard.writeText(captions.tiktok);
                    setCaptionCopied(true);
                    trackShare('caption', 'copy_caption', boothId, event?.event_id);
                    setTimeout(() => setCaptionCopied(false), 2200);
                  } catch { /* ignore */ }
                }}
                style={{
                  minHeight: 48, padding: '0 22px',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: captionCopied
                    ? 'linear-gradient(135deg, rgba(0,212,255,0.32) 0%, rgba(184,151,98,0.32) 100%)'
                    : 'linear-gradient(135deg, rgba(212,175,94,0.22) 0%, rgba(184,151,98,0.22) 100%)',
                  color: '#FFFFFF',
                  border: `1px solid ${captionCopied ? 'rgba(0,212,255,0.7)' : 'rgba(212,175,94,0.55)'}`,
                  borderRadius: 999, fontSize: 14, fontWeight: 800,
                  cursor: 'pointer', letterSpacing: '-0.005em',
                  boxShadow: captionCopied
                    ? '0 0 24px rgba(0,212,255,0.35)'
                    : '0 0 20px rgba(212,175,94,0.25)',
                }}
              >
                {captionCopied ? '✓ Caption copied — paste in your post' : '📋 Copy caption'}
              </button>
            </div>

            <button
              data-testid="booth-360-another-btn"
              onClick={onAnother}
              style={{
                minHeight: 44, padding: '0 22px',
                background: 'transparent', color: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 999, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', letterSpacing: '-0.01em',
              }}
            >
              {kioskMode ? 'Again' : 'Create another spin'}
            </button>

            {/* MADE WITH ATTRIBUTION + EVENT CTA — the viral loop close.
                "Try this at your own event" (NOT "Book now" — wording
                matters per the brief). Subtle but always-visible so
                every shared spin is a top-of-funnel touch. */}
            {!kioskMode && (
              <div
                data-testid="booth-360-viral-cta"
                style={{
                  marginTop: 18, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 8,
                }}
              >
                <a
                  href="/"
                  style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.55)',
                    textDecoration: 'none',
                  }}
                >
                  Made with <span style={{ color: '#FFFFFF' }}>EventDrop</span> Spin Film 360
                </a>
                <p style={{
                  margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.7)',
                  letterSpacing: '-0.01em',
                }}>
                  Want this at your own event?
                </p>
                <a
                  href="/precheckout?package=premium"
                  data-testid="booth-360-try-at-event"
                  style={{
                    minHeight: 48, padding: '0 24px',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)',
                    color: '#FFFFFF', textDecoration: 'none',
                    border: 'none', borderRadius: 999,
                    fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em',
                    boxShadow: '0 12px 30px rgba(184,151,98,0.4)',
                  }}
                >
                  Create your event →
                </a>
              </div>
            )}

            {/* Subtle growth-loop CTA — non-kiosk only. In kiosk the QR
                IS the takeaway moment, no need for a second link. */}
            {!kioskMode && (
              <a
                data-testid="booth-360-host-cta"
                href="https://eventdrop.co"
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 12, color: '#D4AF37',
                  letterSpacing: '0.16em', textTransform: 'uppercase',
                  textDecoration: 'underline', textUnderlineOffset: 5,
                  marginTop: 4,
                }}
              >
                Want one at your event?
              </a>
            )}

            {event?.name && (
              <p style={{
                fontSize: 11, letterSpacing: '0.18em',
                color: 'rgba(255,255,255,0.32)',
                textTransform: 'uppercase', margin: 0,
              }}>
                Saved to {event.name}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR block — kiosk only, shows 0.8s after the buttons reveal.
          Subtly pulses every 5s so it stays visible without nagging. */}
      <AnimatePresence>
        {kioskMode && showQr && shareUrl && (
          <motion.div
            key="qr-block"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease: 'easeOut' }}
            data-testid="booth-360-qr-block"
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 14,
              padding: '20px 22px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 18, marginTop: 6,
            }}
          >
            <p style={{
              margin: 0, fontSize: 12, letterSpacing: '0.18em',
              color: '#D4AF37', textTransform: 'uppercase',
            }}>
              Scan to get your video →
            </p>
            <motion.div
              animate={{ scale: [1, 1.025, 1] }}
              transition={{ duration: 2.4, ease: 'easeInOut', repeat: Infinity, repeatDelay: 2.6 }}
              style={{
                background: '#FFFFFF', padding: 14, borderRadius: 14,
                lineHeight: 0,
              }}
            >
              <QRCodeSVG
                value={shareUrl}
                size={180}
                bgColor="#FFFFFF"
                fgColor="#0A0A0F"
                level="M"
                includeMargin={false}
              />
            </motion.div>
            <p style={{
              margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.5)',
              letterSpacing: '0.04em',
            }}>
              Opens instantly on your phone
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const NotFoundView = () => (
  <div style={{
    minHeight: '100svh', background: '#000', color: '#FFFFFFD9',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'Georgia, serif', textAlign: 'center', padding: 22,
  }}>
    <div>
      <h1 style={{ fontWeight: 400, fontSize: 28, margin: '0 0 8px' }}>Booth not available</h1>
      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', margin: 0 }}>This event link looks expired.</p>
    </div>
  </div>
);

export default Booth360Page;
